import { Link } from "react-router-dom";
import { useState, useMemo } from "react";
import { QRCodeSVG } from "qrcode.react";
import { useTranslation } from "react-i18next";
import {
  AlertTriangle, Sparkles, ShieldCheck, Video, Stethoscope,
  ClipboardCheck, MessageSquare, HeartPulse, Lock, KeyRound,
  ArrowRight, Plus, Minus, Pill, FlaskConical, User, Building2, Truck, Beaker,
  QrCode,
} from "lucide-react";
import { useHotel } from "@/lib/hotel";
import { useAuth } from "@/lib/auth";
import { TID } from "@/constants/testIds/aura";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import heroWorldClass from "@/assets/hero-worldclass.jpg";
import logoMark from "@/assets/logo-allround.png";
import { ROLE_WORKSPACES } from "@/lib/roleWorkspaces";
import { useServerConfig } from "@/lib/serverConfig";

const WORKSPACE_ICONS = { User, Stethoscope, Building2, ShieldCheck, Pill, FlaskConical, Truck, Beaker };

/**
 * Landing page — editorial telemedicine layout inspired by asvclinic.com but adapted
 * to AllRound Concierge's in-room medical concierge context. Emergency banner stays prominent.
 */
export default function Landing() {
  const { ctx } = useHotel();
  const { user, profile } = useAuth();
  const { t } = useTranslation();
  const serverCfg = useServerConfig();
  const showDemoHint =
    process.env.REACT_APP_ENABLE_DEMO_LOGIN === "true" && serverCfg?.demo_mode === true;

  const hotelName = ctx?.hotel?.name;
  const roomNumber = ctx?.room?.room_number;
  const eyebrow = hotelName && roomNumber
    ? t("landing.eyebrowRoom", { hotel: hotelName, room: roomNumber })
    : hotelName
      ? t("landing.eyebrowHotel", { hotel: hotelName })
      : t("landing.eyebrowDefault");

  const isPatient = user?.role === "patient";
  // Feature chips route patients straight to the feature; logged-out
  // visitors go to /register (Protected bounces anonymous users to "/").
  const HERO_FEATURES = [
    { icon: Stethoscope, label: "Talk to a Doctor Anytime", to: isPatient ? "/doctors" : "/register", tid: "hero-feature-doctor" },
    { icon: Pill, label: "Prescriptions & Delivery", to: isPatient ? "/care/finder" : "/register", tid: "hero-feature-prescriptions" },
    { icon: FlaskConical, label: "Lab Tests at Your Door", to: isPatient ? "/care/finder" : "/register", tid: "hero-feature-labs" },
    { icon: ShieldCheck, label: "Secure & Confidential", to: "/compliance", tid: "hero-feature-secure" },
  ];
  const workspaceHome =
    user?.role === "doctor" ? "/doctor"
    : user?.role === "pharmacy" ? "/pharmacy/orders"
    : user?.role === "laboratory" ? "/laboratory/orders"
    : user?.role === "platform_admin" ? "/platform"
    : (user?.role === "hotel_staff" || user?.role === "hotel_manager") ? "/hotel/dashboard"
    : "/emergency";

  // Real, scannable QR for the hero callout. When the guest arrived via a
  // room QR we encode a deep-link back to that room's concierge; otherwise
  // we point at the stay-code guest login so anyone can open the concierge.
  const roomQrCode = ctx?.room?.qr_code;
  const qrDeepLink = useMemo(() => {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    return roomQrCode ? `${origin}/?qr=${roomQrCode}` : `${origin}/login/guest`;
  }, [roomQrCode]);

  return (
    <div className="min-h-screen bg-warm text-stone-900">
      {/* Emergency banner — must stay visible and prominent */}
      <section
        data-testid={TID.emergencyBanner}
        className="bg-emergency border-b border-emergency"
      >
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-start sm:items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-emergency shrink-0 mt-0.5 sm:mt-0" />
          <div className="flex-1 text-sm">
            <span className="text-emergency font-semibold uppercase tracking-wide text-xs">{t("emergency.label")}</span>
            <span className="text-stone-800 ml-2">
              {t("emergency.body")}
            </span>
          </div>
          <a
            href="tel:112"
            className="text-emergency font-semibold text-sm inline-flex items-center gap-1 whitespace-nowrap"
            data-testid="emergency-call-link"
          >
            {t("emergency.cta")} <ArrowRight className="h-3.5 w-3.5" />
          </a>
        </div>
      </section>

      {/* ---------- Header + Hero (full-bleed luxury) ---------- */}
      <section className="relative overflow-hidden bg-[#140e0a]" data-testid="landing-hero">
        <img
          src={heroWorldClass}
          alt="Hotel guest consulting a doctor over secure video from a luxury suite"
          className="absolute inset-0 h-full w-full object-cover object-right"
          loading="eager"
          decoding="async"
        />
        {/* Legibility scrim — dark on the left, fades to reveal the scene on the right */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, rgba(15,10,7,0.96) 0%, rgba(15,10,7,0.93) 44%, rgba(15,10,7,0.78) 60%, rgba(15,10,7,0.4) 80%, rgba(15,10,7,0.12) 100%)",
          }}
        />

        {/* Header nav — overlaid, light on dark */}
        <nav className="relative z-10 max-w-6xl xl:max-w-7xl mx-auto px-6 py-6 flex items-center justify-between text-cream">
          <div className="flex items-center gap-2.5">
            <img src={logoMark} alt="AllRound" className="h-9 w-9 rounded-full object-cover ring-1 ring-white/20" data-testid="landing-logo" />
            <div className="leading-none">
              <span className="font-serif text-xl tracking-tight block text-cream" data-testid={TID.hotelName}>
                AllRound
              </span>
              <span className="text-[9px] uppercase tracking-[0.22em] text-cream/60">Health Concierge</span>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-3">
            <a href="#how" className="text-sm text-cream/80 hover:text-cream transition-colors" data-testid="nav-how-it-works">How it works</a>
            <a href="#workspaces" className="text-sm text-cream/80 hover:text-cream transition-colors" data-testid="nav-workspaces">Workspaces</a>
            <div className="[&_select]:bg-transparent [&_select]:text-cream/80 [&_svg]:text-cream/70">
              <LanguageSwitcher />
            </div>
            {user ? (
              <Link to={workspaceHome} className="btn-primary text-sm py-2" data-testid="nav-continue">
                {t("common.continue")}{profile?.full_name ? ` · ${profile.full_name.split(" ")[0]}` : ""} <ArrowRight className="h-4 w-4" />
              </Link>
            ) : (
              <>
                <Link to="/login" className="text-sm text-cream/90 hover:text-cream transition-colors font-medium" data-testid={TID.landingLogin}>{t("common.signIn")}</Link>
                <Link to="/register" className="btn-primary text-sm py-2" data-testid={TID.landingRegister}>
                  {t("landing.getStarted")} <ArrowRight className="h-4 w-4" />
                </Link>
              </>
            )}
          </div>
        </nav>

        {/* Hero content */}
        <div className="relative z-10 max-w-6xl xl:max-w-7xl mx-auto px-6 pt-8 pb-16 lg:pt-16 lg:pb-24">
          <div className="max-w-2xl text-cream" style={{ textShadow: "0 1px 14px rgba(0,0,0,0.55)" }}>
            {(hotelName || roomNumber) && (
              <div className="eyebrow eyebrow-white mb-4 fade-up" style={{ animationDelay: "0ms" }} data-testid="hero-eyebrow">{eyebrow}</div>
            )}
            <h1 className="font-serif text-[2.75rem] leading-[1.03] sm:text-6xl lg:text-[4.25rem] tracking-tight text-cream fade-up" style={{ animationDelay: "60ms" }}>
              World-Class Care,<br />
              <span style={{ color: "#D8B87A" }}>Wherever You Stay</span>
            </h1>
            <p className="mt-6 text-lg text-cream/80 leading-relaxed max-w-xl fade-up" style={{ animationDelay: "140ms" }}>
              24/7 access to trusted doctors, prescriptions, labs, and more — from the comfort of your hotel room.
            </p>
            {roomNumber && (
              <p className="mt-3 text-sm text-cream/70 fade-up" style={{ animationDelay: "180ms" }} data-testid={TID.roomNumber}>
                You are checked in from <span className="font-semibold text-cream">Room {roomNumber}</span>.
              </p>
            )}

            {/* Feature chips — each wired to its page */}
            <div className="mt-9 flex flex-wrap gap-x-8 gap-y-5 fade-up" style={{ animationDelay: "220ms" }} data-testid="hero-features">
              {HERO_FEATURES.map((f) => {
                const Icon = f.icon;
                return (
                  <Link
                    key={f.tid}
                    to={f.to}
                    data-testid={f.tid}
                    className="group flex items-center gap-3 text-left"
                  >
                    <span className="h-10 w-10 rounded-full border flex items-center justify-center shrink-0 transition-colors"
                          style={{ borderColor: "rgba(216,184,122,0.5)", color: "#D8B87A" }}>
                      <Icon className="h-4.5 w-4.5" />
                    </span>
                    <span className="text-sm font-medium text-cream/90 leading-tight max-w-[7.5rem] group-hover:text-cream border-b border-transparent group-hover:border-cream/40 pb-0.5">
                      {f.label}
                    </span>
                  </Link>
                );
              })}
            </div>

            {/* CTAs */}
            <div className="mt-9 flex flex-wrap gap-3 fade-up" style={{ animationDelay: "320ms" }}>
              {user ? (
                <Link to={workspaceHome} className="btn-primary" data-testid="hero-continue-cta">
                  Continue{profile?.full_name ? ` as ${profile.full_name.split(" ")[0]}` : ""} <ArrowRight className="h-4 w-4" />
                </Link>
              ) : roomNumber ? (
                // Arrived via room QR — lead with the concierge "now" action.
                <>
                  <Link to="/register" className="btn-primary" data-testid="cta-need-doctor-now">
                    I need a doctor now <ArrowRight className="h-4 w-4" />
                  </Link>
                  <Link to="/register" className="btn-primary" style={{ background: "#D8B87A", color: "#2A1E12" }} data-testid="cta-book-consultation">
                    Book a consultation
                  </Link>
                  <Link
                    to="/login/guest"
                    className="inline-flex items-center gap-2 rounded-full border border-cream/30 px-5 py-2.5 text-sm font-medium text-cream hover:bg-cream/10 transition-colors"
                    data-testid="cta-access-health-account"
                  >
                    Access my health account
                  </Link>
                </>
              ) : (
                <>
                  <Link to="/register" className="btn-primary" data-testid="landing-get-care">
                    Get care now <ArrowRight className="h-4 w-4" />
                  </Link>
                  <Link
                    to="/login/guest"
                    className="inline-flex items-center gap-2 rounded-full border border-cream/30 px-5 py-2.5 text-sm font-medium text-cream hover:bg-cream/10 transition-colors"
                    data-testid="hero-guest-login"
                  >
                    I have a room code
                  </Link>
                </>
              )}
            </div>

            {/* QR callout */}
            {/* QR callout — real scannable code (opens the room concierge) */}
            <a
              href={qrDeepLink}
              className="group mt-10 inline-flex items-center gap-4 rounded-2xl px-5 py-4 fade-up transition-colors"
              style={{ border: "1px solid rgba(216,184,122,0.4)", background: "rgba(216,184,122,0.06)", animationDelay: "400ms" }}
              data-testid="hero-qr-callout"
            >
              <span className="shrink-0 rounded-xl bg-white p-2" data-testid="hero-qr-code">
                <QRCodeSVG value={qrDeepLink} size={72} level="M" fgColor="#2A1E12" bgColor="#ffffff" />
              </span>
              <div className="text-sm leading-snug">
                <span style={{ color: "#D8B87A" }} className="font-semibold">
                  {roomNumber ? `Scan to open Room ${roomNumber} concierge` : "Scan the QR in your room"}
                </span>
                <span className="text-cream/75"> — instant access to your private Medical Concierge.</span>
              </div>
            </a>

            {/* Trust row */}
            <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 fade-up" style={{ animationDelay: "480ms" }}>
              <TrustStat n="50+" l="Specialists on call" dark />
              <TrustStat n="< 10m" l="Avg. wait" dark />
              <TrustStat n="100%" l="Encrypted" dark />
            </div>
          </div>
        </div>
      </section>

      {/* ---------- Trust strip ---------- */}
      <section className="border-y border-[rgba(33,59,42,0.10)] bg-white/50">
        <div className="max-w-6xl mx-auto px-6 py-8">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6 items-center text-center">
            <TrustBadge icon={<Lock className="h-4 w-4" />} label="HIPAA aligned" />
            <TrustBadge icon={<ShieldCheck className="h-4 w-4" />} label="GDPR ready" />
            <TrustBadge icon={<KeyRound className="h-4 w-4" />} label="Encrypted in transit" />
            <TrustBadge icon={<ClipboardCheck className="h-4 w-4" />} label="Signed & immutable outputs" />
            <TrustBadge icon={<Stethoscope className="h-4 w-4" />} label="Hotel-partnered clinicians" />
          </div>
        </div>
      </section>

      {/* ---------- How it works ---------- */}
      <section id="how" className="max-w-6xl mx-auto px-6 py-20 lg:py-28">
        <div className="max-w-2xl">
          <div className="eyebrow">How it works</div>
          <h2 className="font-serif text-4xl sm:text-5xl leading-tight mt-3 text-stone-900">
            From symptom to specialist in <span className="italic">three quiet steps.</span>
          </h2>
          <p className="mt-4 text-stone-600 leading-relaxed">
            Designed so a guest anywhere in the hotel can reach a real physician without
            leaving the room — no clinic queue, no lobby explanation.
          </p>
        </div>

        <div className="mt-14 grid md:grid-cols-3 gap-6 lg:gap-8">
          <StepCard n="1" title="Triage" desc="A few gentle questions with our AllRound Health Concierge assistant quietly match you to the right specialty." variant="teal" />
          <StepCard n="2" title="Consult" desc="Meet your specialist over secure video, from your room, in minutes — not tomorrow." variant="coral" />
          <StepCard n="3" title="Follow up" desc="Signed prescriptions, lab orders and secure messages arrive in your private record." variant="sunshine" />
        </div>
      </section>

      {/* ---------- AI triage preview / conditions block replacement ---------- */}
      <section className="bg-forest text-cream relative overflow-hidden">
        <div className="grain" />
        <div className="max-w-6xl mx-auto px-6 py-20 lg:py-28 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="eyebrow eyebrow-white">AI-assisted triage</div>
              <h2 className="font-serif text-4xl sm:text-5xl leading-tight mt-3">
                <span className="italic">Not a chat thread.</span><br />
                A calm conversation that gets you to the right specialist.
              </h2>
              <p className="mt-5 text-cream/75 leading-relaxed max-w-lg">
                Powered by Claude Sonnet, tuned for gentle triage. It never diagnoses — it
                simply routes you to the physician best suited to your symptoms.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                {user ? (
                  <Link to="/emergency" className="btn-primary" style={{ background: "#F7F4ED", color: "#3A5141" }}>
                    Start triage <ArrowRight className="h-4 w-4" />
                  </Link>
                ) : (
                  <Link to="/register" className="btn-primary" style={{ background: "#F7F4ED", color: "#3A5141" }}>
                    Try it now <ArrowRight className="h-4 w-4" />
                  </Link>
                )}
              </div>
            </div>

            {/* Preview chat card */}
            <div className="fade-up" style={{ animationDelay: "80ms" }}>
              <div className="rounded-[1.75rem] bg-cream/95 text-stone-900 p-6 shadow-2xl border border-white/10">
                <div className="flex items-center gap-2 pb-3 border-b border-[rgba(33,59,42,0.10)]">
                  <Sparkles className="h-4 w-4 text-sage-500" />
                  <div className="text-xs uppercase tracking-widest text-sage-500">AllRound triage · demo</div>
                </div>
                <div className="mt-4 space-y-2.5 text-sm">
                  <ChatLine mine={false} text="Hello. In one or two sentences, what's going on today?" />
                  <ChatLine mine={true}  text="I've had a throbbing headache since I landed. It won't ease with water or rest." />
                  <ChatLine mine={false} text="Any nausea or sensitivity to light? On a 1–10, how intense is it?" />
                  <ChatLine mine={true}  text="Some nausea. Maybe a 7." />
                  <div className="pt-3 mt-3 border-t border-[rgba(33,59,42,0.10)]">
                    <div className="text-[10px] uppercase tracking-widest text-sage-500">Suggested specialty</div>
                    <div className="font-serif text-2xl text-stone-900 mt-0.5">Neurology</div>
                    <div className="text-xs text-stone-500 mt-1">2 doctors available now · from your room</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- Why choose us ---------- */}
      <section className="max-w-6xl mx-auto px-6 py-20 lg:py-28">
        <div className="max-w-2xl">
          <div className="eyebrow">Why choose AllRound</div>
          <h2 className="font-serif text-4xl sm:text-5xl leading-tight mt-3 text-stone-900">
            Built for guests who expect more than <span className="italic">a chat thread.</span>
          </h2>
        </div>
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          <Feature icon={<MessageSquare className="h-4 w-4" />} title="No WhatsApp"     desc="Consultations happen on software approved for telemedicine, not a personal messenger." />
          <Feature icon={<Video className="h-4 w-4" />}         title="Room-first"       desc="Nothing to install. Join a secure video call from any device on hotel Wi-Fi." />
          <Feature icon={<Lock className="h-4 w-4" />}          title="Private by design" desc="Hotel staff see room number and status only — never symptoms, never diagnoses." />
          <Feature icon={<ClipboardCheck className="h-4 w-4" />}title="Signed outputs"  desc="Prescriptions and lab orders are HMAC-signed and independently verifiable." />
          <Feature icon={<Pill className="h-4 w-4" />}          title="Delivered to you" desc="Your signed Rx routes to a partner pharmacy of your choosing. No lobby errands." />
          <Feature icon={<FlaskConical className="h-4 w-4" />}  title="Lab-integrated"   desc="Lab orders route to a partner laboratory; results appear in your private record." />
        </div>
      </section>

      {/* ---------- Emergency-safe callout (replaces "Watch in action") ---------- */}
      <section className="max-w-6xl mx-auto px-6 pb-20 lg:pb-28">
        <div className="card-cream p-8 sm:p-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <div className="eyebrow">Non-emergency, always</div>
            <h3 className="font-serif text-3xl sm:text-4xl mt-2 leading-tight">
              For anything urgent, call your local emergency number.
            </h3>
            <p className="mt-3 text-stone-600 max-w-2xl">
              AllRound Health Concierge is for consultations, follow-ups and second opinions — not chest pain, severe
              bleeding or unresponsive guests. Please dial the local emergency line first.
            </p>
          </div>
          <a href="tel:112" className="btn-secondary shrink-0" style={{ borderColor: "#B91C1C", color: "#B91C1C" }}>
            <AlertTriangle className="h-4 w-4" /> Emergency numbers
          </a>
        </div>
      </section>

      {/* ---------- Sign in to your workspace ---------- */}
      <section className="max-w-6xl mx-auto px-6 py-20 lg:py-28" id="workspaces" data-testid="workspace-picker">
        <div className="max-w-2xl">
          <div className="eyebrow">Every role, its own view</div>
          <h2 className="font-serif text-4xl sm:text-5xl leading-tight mt-3 text-stone-900">
            Sign in to <span className="italic">your workspace.</span>
          </h2>
          <p className="mt-4 text-stone-600 leading-relaxed">
            Each role has its own sign-in and its own view of the case. Nothing is shared by default.
          </p>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {ROLE_WORKSPACES.map((w) => {
            const Icon = WORKSPACE_ICONS[w.icon] || User;
            return (
              <Link
                key={w.slug}
                to={`/login/${w.slug}`}
                className="workspace-card group"
                data-testid={`workspace-card-${w.slug}`}
              >
                <div className="flex items-center justify-between">
                  <span className="h-10 w-10 rounded-full bg-brand-soft text-forest flex items-center justify-center">
                    <Icon className="h-4.5 w-4.5" />
                  </span>
                  <ArrowRight className="h-4 w-4 text-stone-400 group-hover:text-forest group-hover:translate-x-0.5 transition" />
                </div>
                <div className="font-serif text-xl text-stone-900 mt-4">{w.label}</div>
                <div className="text-xs text-stone-500 italic mt-0.5">{w.org}</div>
                <p className="text-sm text-stone-600 mt-2.5 leading-relaxed">{w.description}</p>
                <div className="mt-4 text-[10px] uppercase tracking-widest font-semibold text-accent-warm">
                  {w.authNote}
                </div>
              </Link>
            );
          })}
        </div>
        {showDemoHint && (
          <div className="mt-6 text-xs text-stone-500" data-testid="demo-credentials-hint">
            Demo hotel manager login — email <code className="text-stone-700">manager@aura.demo</code>, password <code className="text-stone-700">Demo123!</code>
          </div>
        )}
      </section>

      {/* ---------- FAQ ---------- */}
      <section className="max-w-6xl mx-auto px-6 pb-24">
        <div className="grid lg:grid-cols-3 gap-10">
          <div>
            <div className="eyebrow">Questions, answered</div>
            <h2 className="font-serif text-4xl sm:text-5xl leading-tight mt-3">
              Everything guests <span className="italic">usually ask.</span>
            </h2>
          </div>
          <div className="lg:col-span-2 space-y-3">
            <Faq q="How quickly can I see a doctor?">
              Instant consultations reach an on-call specialist within about ten minutes.
              Scheduled appointments show live availability by the hour.
            </Faq>
            <Faq q="Can the hotel see my symptoms or diagnosis?">
              No. Hotel staff surfaces are limited to your room number, booking status and timestamps.
              Clinical fields are locked to your care team by design and enforced in code.
            </Faq>
            <Faq q="What happens to my prescription?">
              Once your doctor signs it, the prescription is cryptographically immutable. You choose
              which partner pharmacy fulfils it — they can update status but never edit clinical content.
            </Faq>
            <Faq q="Is my data encrypted?">
              Yes. All traffic uses TLS in transit and encrypted storage at rest. Every clinical
              output ships with a verifiable SHA-256 + HMAC signature.
            </Faq>
            <Faq q="Which specialties are covered?">
              General practice, internal medicine, ENT, dermatology and more — the roster depends
              on your hotel&#39;s local network of vetted physicians.
            </Faq>
          </div>
        </div>
      </section>

      {/* ---------- Footer ---------- */}
      <footer className="bg-forest text-cream/80">
        <div className="max-w-6xl mx-auto px-6 py-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          <div>
            <div className="flex items-center gap-2">
              <img src={logoMark} alt="AllRound" className="h-9 w-9 rounded-full object-cover" />
              <span className="font-serif text-xl text-cream tracking-tight">AllRound Health Concierge</span>
            </div>
            <p className="mt-4 text-sm leading-relaxed">
              In-room medical concierge for hotel guests. Non-emergency virtual care, signed
              clinical outputs, private by design.
            </p>
          </div>
          <FooterCol title="Product" items={[
            { label: "AI triage", to: "/triage" },
            { label: "Find a doctor", to: "/doctors" },
            { label: "Your care record", to: "/guest" },
          ]} />
          <FooterCol title="For partners" items={[
            { label: "Doctor sign in", to: "/login/doctor" },
            { label: "Pharmacy sign in", to: "/login/pharmacy" },
            { label: "Laboratory sign in", to: "/login/laboratory" },
            { label: "Hotel staff", to: "/login/hotel-staff" },
          ]} />
          <FooterCol title="Compliance" items={[
            { label: "HIPAA aligned" },
            { label: "GDPR ready" },
            { label: "Encrypted in transit & at rest" },
            { label: "Signed clinical outputs" },
          ]} />
        </div>
        <div className="border-t border-cream/10">
          <div className="max-w-6xl mx-auto px-6 py-6 text-xs text-cream/50 flex flex-col sm:flex-row justify-between gap-2">
            <div>© {new Date().getFullYear()} AllRound Health Concierge · Non-emergency virtual care</div>
            <div>For life-threatening emergencies dial your local emergency number.</div>
          </div>
        </div>
      </footer>
    </div>
  );
}

/* ---------- Small subcomponents ---------- */

function TrustStat({ n, l, dark = false }) {
  return (
    <div>
      <div className={`font-serif text-3xl ${dark ? "text-cream" : "text-stone-900"}`}>{n}</div>
      <div className={`text-xs uppercase tracking-widest mt-0.5 ${dark ? "text-cream/60" : "text-stone-500"}`}>{l}</div>
    </div>
  );
}

function TrustBadge({ icon, label }) {
  return (
    <div className="inline-flex items-center gap-2 text-stone-700 justify-center rise-in">
      <span className="h-7 w-7 rounded-full bg-brand-soft text-forest flex items-center justify-center">{icon}</span>
      <span className="text-sm font-medium">{label}</span>
    </div>
  );
}

function StepCard({ n, title, desc, variant = "teal" }) {
  const map = {
    teal:     { grad: "grad-lagoon",   chip: "bg-brand-soft text-forest" },
    coral:    { grad: "grad-sunrise",  chip: "bg-coral-soft text-coral" },
    sunshine: { grad: "bg-sunshine",   chip: "bg-sunshine-soft text-sunshine" },
  }[variant] || { grad: "grad-lagoon", chip: "bg-brand-soft text-forest" };
  return (
    <div className="glass-lux rounded-2xl p-7 hover:-translate-y-1 transition-transform">
      <div className={`h-12 w-12 rounded-2xl ${map.grad} text-white flex items-center justify-center font-serif text-2xl shadow-[0_10px_24px_-10px_rgba(9,95,88,0.35)]`}>
        {n}
      </div>
      <div className={`mt-4 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest font-semibold ${map.chip}`}>
        Step {n}
      </div>
      <h3 className="font-serif text-2xl text-stone-900 mt-3">{title}</h3>
      <p className="text-stone-600 mt-2 leading-relaxed">{desc}</p>
    </div>
  );
}

function Feature({ icon, title, desc }) {
  return (
    <div className="flex gap-4 items-start">
      <div className="h-10 w-10 rounded-full bg-[rgba(33,59,42,0.08)] flex items-center justify-center text-sage-500 shrink-0">
        {icon}
      </div>
      <div>
        <div className="font-serif text-xl text-stone-900">{title}</div>
        <div className="text-stone-600 text-sm mt-1 leading-relaxed">{desc}</div>
      </div>
    </div>
  );
}

function ChatLine({ mine, text }) {
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[85%] px-3.5 py-2 rounded-2xl ${mine ? "bubble-user rounded-br-sm" : "bubble-ai rounded-bl-sm"}`}>
        {text}
      </div>
    </div>
  );
}

function Faq({ q, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-[rgba(33,59,42,0.10)] rounded-2xl bg-white overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
        data-testid="faq-toggle"
      >
        <span className="font-serif text-lg sm:text-xl text-stone-900">{q}</span>
        <span className="h-8 w-8 rounded-full bg-[rgba(33,59,42,0.06)] text-sage-500 flex items-center justify-center shrink-0">
          {open ? <Minus className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
        </span>
      </button>
      {open && (
        <div className="px-5 pb-5 text-stone-600 leading-relaxed text-sm border-t border-[rgba(33,59,42,0.10)] pt-4">
          {children}
        </div>
      )}
    </div>
  );
}

function FooterCol({ title, items }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-[0.2em] font-semibold text-cream/60">{title}</div>
      <ul className="mt-4 space-y-2">
        {items.map((it, i) => (
          <li key={i}>
            {it.to ? (
              <Link to={it.to} className="text-sm hover:text-cream transition-colors">{it.label}</Link>
            ) : (
              <span className="text-sm">{it.label}</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
